import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, MapPin, Phone } from "lucide-react";
import { Overline, Reveal } from "./Shared";
import { waLink, WHATSAPP_NUMBER } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/35 focus:border-gold focus:outline-none transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-ink" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <div>
          <Overline>Enquiries & Booking</Overline>
          <h2 className="mt-5 font-heading font-light text-4xl md:text-6xl text-white leading-[1.02]">
            Reserve your <span className="italic text-gold">chauffeur</span>
          </h2>
          <p className="mt-6 text-white/55 text-base leading-relaxed max-w-md">
            Send us the details and we'll confirm a fixed price within the hour. For an instant response, message us
            directly on WhatsApp.
          </p>

          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="mt-10 inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-gold-light hover:text-ink transition-colors duration-300"
          >
            <MessageCircle size={18} /> Chat on WhatsApp
          </a>

          <div className="mt-12 space-y-5 text-sm">
            <a
              href={`tel:+${WHATSAPP_NUMBER}`}
              className="flex items-center gap-4 text-white/70 hover:text-gold transition-colors"
              data-testid="contact-phone"
            >
              <Phone size={18} className="text-gold" /> +44 7539 889920
            </a>
            <p className="flex items-start gap-4 text-white/70">
              <MapPin size={18} className="text-gold mt-0.5" />
              85 High Street, Stanwell, Staines-Upon-Thames, England, TW19 7LJ
            </p>
          </div>
        </div>

        <Reveal>
          <form
            onSubmit={submit}
            className="rounded-sm border border-white/10 bg-charcoal p-8 md:p-10 space-y-8"
            data-testid="contact-form"
          >
            <div>
              <label className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className={FIELD}
                data-testid="contact-name"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className={FIELD}
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Phone (optional)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+44 …"
                  className={FIELD}
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Journey Details</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Pick-up, destination, date & time…"
                className={`${FIELD} resize-none`}
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-gold-fill w-full inline-flex items-center justify-center gap-2 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-widest uppercase rounded-sm hover:text-ink disabled:opacity-60"
            >
              <Send size={16} /> {loading ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
